import { useEffect, useRef, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Tick02Icon, ArrowLeft01Icon, ArrowRight01Icon } from '@hugeicons/core-free-icons';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/Checkbox';
import { Button } from '@/components/ui/button';
import { log } from '@/lib/logger';
import { isLocalUrl } from '@/lib/utils';
import { onboardingWebsiteError } from '@/lib/onboardingWebsite';
import { isLocalFilePath, ONBOARDING_LOCAL_FILE_MESSAGE, SANDBOX_DEMO_PAGE_URL, normalizeOnboardingWebsiteUrl, ONBOARDING_WEBSITE_URL_ERROR } from '@overlayqa/shared';

/** Use the same website gate as the final save and the server. */
function isValidUrl(value: string): boolean {
  return normalizeOnboardingWebsiteUrl(value) !== null;
}

// isLocalUrl moved to lib/utils.ts so the dashboard's RecentProjectsSection can
// share it; re-exported here to keep this module's public API stable.
export { isLocalUrl };

/** Extract a human-readable project name from a URL domain. */
function deriveProjectName(url: string): string {
  try {
    const withProtocol = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const { hostname } = new URL(withProtocol);
    // Strip www. prefix and use the domain as the name
    const domain = hostname.replace(/^www\./, '');
    // Capitalize first letter of first segment
    const name = domain.split('.')[0];
    return name.charAt(0).toUpperCase() + name.slice(1);
  } catch {
    return '';
  }
}

/**
 * The name shown as a ghost when there is no URL to derive one from. Mirrors
 * the server's own terminal fallback in onboarding.ts (`projectName ||
 * primaryDomain || 'My Project'`), so the two can't disagree.
 */
export const DEFAULT_PROJECT_NAME = 'My Project';

/**
 * What Finish actually sends as the project name.
 *
 * The derived name lives in the PLACEHOLDER, never the value (see the ghost
 * comment in StepProject), so a user who leaves the field alone submits a blank
 * name. Sending blank would let the server fall back to the full hostname
 * ("staging.acme.io") rather than the ghost the user was shown ("Staging"), so
 * this resolves the ghost here and keeps that promise. Undefined when there is
 * nothing to derive, which leaves the server's own fallback chain intact.
 */
export function resolveProjectName(projectName: string, projectUrl: string): string | undefined {
  const typed = projectName.trim();
  if (typed) return typed;
  const url = projectUrl.trim();
  if (url && isValidUrl(url)) {
    return deriveProjectName(url) || undefined;
  }
  return undefined;
}

const URL_CHIPS = [
  { label: 'SaaS Demo', url: SANDBOX_DEMO_PAGE_URL, type: 'saas_demo' as const },
];

/**
 * Current and former demo hosts must not promise a weekly scan.
 *
 * Mirrors the server's own chip map (server lib/chipThumbnails.ts), which the
 * re-scan eligibility filter reads for the same reason: the one copy of this
 * list that WAS retyped missed the Shopify chip, and a real user was enrolled
 * in a weekly re-scan of shopify.com on 2026-08-14.
 */
const CHIP_HOSTS = new Set([
  ...URL_CHIPS.map((chip) => new URL(chip.url).hostname.replace(/^www\./, '')),
  // Keep the server's historical demo exclusions even after removing the chips.
  'notion.so',
  'shopify.com',
]);

/** Is this URL one of the demo chips rather than the user's own site? */
function isDemoChipUrl(value: string): boolean {
  try {
    const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    return CHIP_HOSTS.has(new URL(withProtocol).hostname.replace(/^www\./, ''));
  } catch {
    return false;
  }
}

export type UrlType = 'own' | 'saas_demo' | 'notion' | 'shopify';

interface StepProjectProps {
  projectName: string;
  projectUrl: string;
  onChangeName: (name: string) => void;
  onChangeUrl: (url: string) => void;
  onUrlTypeChange?: (type: UrlType) => void;
  /** Weekly re-scan opt-in. Defaults to checked; see the checkbox comment. */
  scheduledScanOptIn?: boolean;
  onScheduledScanOptInChange?: (optIn: boolean) => void;
  /**
   * Asks whether the WEEKLY SCAN can reach the URL (POST /onboarding/scan-preflight),
   * once the field settles. The box promises an email; measured 2026-10-02 on a
   * real signup, the site answered 403 to our headless scanner while letting
   * the review preflight through, so the promise could never be kept and
   * nothing said so. Optional: tests and surfaces without a token pass nothing.
   */
  onScanPreflight?: (url: string) => Promise<{ scannable: boolean; reason: string }>;
  /**
   * The primary action, run only after the URL gate below passes: Finish when
   * this is the wizard's last step, Continue (to the invite step) otherwise.
   * The name stayed `onFinish` so the gate's existing callers and tests read
   * unchanged; `isLastStep` picks the label.
   */
  onFinish: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  /**
   * False when the invite step follows (2026-09-09): the button reads Continue
   * with StepWelcome's arrow and nothing is saved or sent from here. Defaults
   * to true, the shape every earlier caller had.
   */
  isLastStep?: boolean;
}

export function StepProject({
  projectName,
  projectUrl,
  onChangeName,
  onChangeUrl,
  onUrlTypeChange,
  scheduledScanOptIn = true,
  onScheduledScanOptInChange,
  onScanPreflight,
  onFinish,
  onBack,
  isSubmitting,
  isLastStep = true,
}: StepProjectProps) {
  const [urlTouched, setUrlTouched] = useState(false);
  // Set when Finish is clicked without a usable URL. The button stays clickable
  // and answers with the guard message instead of a silent disabled state: a
  // hard-disabled Finish swallows the click and gives the user nothing to act
  // on (the wizard-level guard in OnboardingWizard.handleFinish was unreachable
  // behind it).
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const urlInputRef = useRef<HTMLInputElement>(null);
  // A URL already in the field at mount (pre-filled from a free tool, or a restored draft) is often longer than the
  // field on a phone, and autoFocus puts the caret at its end, so the field read "aze-pebble-…" (2026-11-01 phone
  // pass). Show its start. type="url" has no selection API, so scroll the field instead of moving the caret.
  useEffect(() => {
    const el = urlInputRef.current;
    if (!el?.value) return;
    const frame = requestAnimationFrame(() => { el.scrollLeft = 0; });
    return () => cancelAnimationFrame(frame);
  }, []);
  // A preset chip is the one thing that writes a real VALUE into the name field
  // (the derived name is a ghost/placeholder instead — see below). Tracked so a
  // later URL edit can drop the now-stale chip label rather than saving a
  // project called "SaaS Demo" that points at the user's own site.
  const [nameFromChip, setNameFromChip] = useState(false);
  const trimmedUrl = projectUrl.trim();
  const ownAppError = onboardingWebsiteError(trimmedUrl);
  const hasValidUrl = trimmedUrl !== '' && isValidUrl(trimmedUrl) && !ownAppError;
  const urlError = urlTouched && trimmedUrl && !isValidUrl(trimmedUrl)
    ? (isLocalFilePath(trimmedUrl)
      ? ONBOARDING_LOCAL_FILE_MESSAGE
      : ONBOARDING_WEBSITE_URL_ERROR)
    : null;
  // Same copy as the OnboardingWizard.handleFinish backstop, so the inline and
  // banner variants of this guard can never say different things.
  const emptyUrlError = submitAttempted && trimmedUrl === ''
    ? 'Please enter a website URL to continue.'
    : null;
  const urlFieldError = ownAppError ?? urlError ?? emptyUrlError;
  const isLocalEntry = hasValidUrl && isLocalUrl(trimmedUrl);
  // GHOST NAME. The name derived from the URL is shown as the placeholder and is
  // never written into the value. Writing it into the value (the behaviour up to
  // 2026-08-25) re-derived on every keystroke, so the field visibly churned while
  // typing, and because the derive was gated on a non-empty URL, backspacing the
  // URL to empty skipped the update and stranded the last fragment: typing
  // "overlayqa.com" then clearing it left the name frozen at "O". A placeholder
  // has neither problem — nobody watches a placeholder change, and it empties
  // with the URL. resolveProjectName() above keeps the promise at submit.
  const derivedName = hasValidUrl ? deriveProjectName(trimmedUrl) : '';
  const showsGhostName = projectName.trim() === '' && derivedName !== '';
  // Offer the weekly scan only when we can actually deliver it: a real,
  // remote, non-chip URL. A local address is unreachable from our scanners and
  // a chip is not the user's site, so showing the box there would promise an
  // email that either never arrives or is about somebody else's website.
  const canOfferScheduledScan = hasValidUrl && !isLocalEntry && !isDemoChipUrl(trimmedUrl);
  // Weekly-scan reachability, asked 800ms after the URL settles. Both
  // callbacks ride refs so a parent that re-creates them on every render
  // cannot reset the debounce or re-probe; the effect keys on the URL alone.
  const SCAN_PREFLIGHT_DEBOUNCE_MS = 800;
  const scanPreflightRef = useRef(onScanPreflight);
  scanPreflightRef.current = onScanPreflight;
  const optInChangeRef = useRef(onScheduledScanOptInChange);
  optInChangeRef.current = onScheduledScanOptInChange;
  const scanPreflightSeq = useRef(0);
  const [scanUnreachable, setScanUnreachable] = useState(false);
  useEffect(() => {
    setScanUnreachable(false);
    if (!scanPreflightRef.current || !canOfferScheduledScan) return;
    const seq = ++scanPreflightSeq.current;
    const target = /^https?:\/\//i.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`;
    const timer = setTimeout(() => {
      scanPreflightRef.current?.(target)
        .then((result) => {
          if (seq !== scanPreflightSeq.current) return; // the URL moved on
          log.info('[StepProject] scan preflight answered', { target, ...result });
          if (!result.scannable && result.reason === 'unreachable') {
            setScanUnreachable(true);
            optInChangeRef.current?.(false);
          }
        })
        .catch((err: unknown) => {
          // Fail open: a probe we could not run says nothing about the site.
          log.warn('[StepProject] scan preflight failed open', { target, error: err instanceof Error ? err.message : String(err) });
        });
    }, SCAN_PREFLIGHT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmedUrl, canOfferScheduledScan]);
  // A preset chip stays "selected" only while both the URL and the project
  // name still match it. Editing either field clears the highlight.
  const selectedChipType = URL_CHIPS.find(
    (chip) => chip.url === trimmedUrl && chip.label === projectName.trim(),
  )?.type;
  log.info('[StepProject] render', { projectName, projectUrl, selectedChipType });

  function handleChipClick(chip: typeof URL_CHIPS[number]) {
    log.info('[StepProject] chip clicked', { label: chip.label, url: chip.url });
    onChangeUrl(chip.url);
    onUrlTypeChange?.(chip.type);
    // A preset carries its own name — use the chip label as the project name
    onChangeName(chip.label);
    setNameFromChip(true);
    setUrlTouched(true);
  }

  function handleUrlChange(value: string) {
    log.info('[StepProject] project url changed', { value });
    onChangeUrl(value);
    onUrlTypeChange?.('own');
    // The derived name is a ghost now, so nothing to write here. The one value
    // this field can hold without the user typing it is a chip label, and once
    // the URL no longer matches that chip the label is stale — drop it so the
    // ghost takes over again.
    if (nameFromChip) {
      onChangeName('');
      setNameFromChip(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="hero-title">What site do you want to review?</h2>
        <p className="mt-2 text-gray-500">
          Enter any URL: local, staging, or production
        </p>
      </div>

      <div className="space-y-6">
        {/* URL field — primary */}
        <div className="space-y-2">
          <label
            htmlFor="onboarding-project-url"
            className="field-label"
          >
            Website URL
          </label>
          <Input
            id="onboarding-project-url"
            ref={urlInputRef}
            type="url"
            value={projectUrl}
            onChange={(e) => handleUrlChange(e.target.value)}
            onBlur={() => setUrlTouched(true)}
            placeholder="https://yoursite.com"
            autoFocus
            disabled={isSubmitting}
            error={!!urlFieldError}
            aria-invalid={!!urlFieldError}
            aria-describedby={urlFieldError ? 'onboarding-project-url-error' : undefined}
          />
          {urlFieldError && (
            <p id="onboarding-project-url-error" className="text-sm text-red-600" role="alert">{urlFieldError}</p>
          )}
          {!urlFieldError && isLocalEntry && (
            <p className="text-sm text-gray-500">
              Local URLs are captured through the browser extension. We'll open your site automatically once it's installed.
            </p>
          )}
          {/*
            Weekly re-scan opt-in.

            CHECKED BY DEFAULT on purpose. Onboarding already enrolled every
            own-URL signup in this watch; it just never said so, and the user
            found out when the first email landed. Checked preserves the
            retention loop while finally disclosing it at the moment the
            promise is made, and unchecking is one click here plus the Schedule
            page, the one-click unsubscribe, and the stop link in every email.
          */}
          {!urlFieldError && canOfferScheduledScan && (
            <div className="flex items-start gap-2 pt-1">
              <Checkbox
                id="onboarding-scheduled-scan-opt-in"
                className="mt-1 shrink-0"
                checked={scheduledScanOptIn}
                disabled={isSubmitting}
                onChange={(e) => onScheduledScanOptInChange?.(e.target.checked)}
              />
              <div className="space-y-1">
                <label
                  htmlFor="onboarding-scheduled-scan-opt-in"
                  className="block text-sm text-gray-700 cursor-pointer"
                >
                  Scan this site weekly and email me what changed
                </label>
                <p className="text-xs text-gray-500">Change or turn this off any time in Schedule.</p>
                {scanUnreachable && (
                  <p className="text-xs text-amber-700" role="status">
                    We can't reach this site from our scanner, so the weekly scan won't run.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* URL suggestion chips */}
        <div className="space-y-2">
          {/*
            Names the chips as DEMOS. Without that word the row reads as "here
            are some sites", and the server then treats the pick as the user's
            own site. Saying "demo" up front also makes the absence of the
            weekly-scan checkbox below legible rather than looking broken.
          */}
          <p className="text-xs text-gray-600">Don't have one handy? Try a demo site</p>
          <div className="flex gap-2">
            {URL_CHIPS.map((chip) => {
              const isSelected = chip.type === selectedChipType;
              return (
                <button
                  key={chip.type}
                  type="button"
                  aria-pressed={isSelected}
                  className={`px-3 py-2 text-xs font-medium rounded-full border transition-colors disabled:opacity-50 ${
                    isSelected
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-600 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50'
                  }`}
                  onClick={() => handleChipClick(chip)}
                  disabled={isSubmitting}
                >
                  <span className="text-cap-trim block">{chip.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Project name — secondary */}
        <div className="space-y-2">
          <label
            htmlFor="onboarding-project-name"
            className="field-label"
          >
            Project name {hasValidUrl && <span className="text-gray-400 font-normal">(optional)</span>}
          </label>
          <Input
            id="onboarding-project-name"
            type="text"
            value={projectName}
            onChange={(e) => {
              log.info('[StepProject] project name changed', { value: e.target.value });
              setNameFromChip(false);
              onChangeName(e.target.value);
            }}
            placeholder={derivedName || DEFAULT_PROJECT_NAME}
            maxLength={200}
            disabled={isSubmitting}
          />
          {showsGhostName && (
            <p className="text-sm text-gray-500">
              Leave blank and we&apos;ll call it &quot;{derivedName}&quot;.
            </p>
          )}
        </div>
      </div>

      <Button
        variant="primary"
        size="md"
        className="w-full"
        disabled={isSubmitting}
        onClick={() => {
          if (!hasValidUrl) {
            log.info('[StepProject] finish blocked: missing or invalid url', { projectUrl });
            setUrlTouched(true);
            setSubmitAttempted(true);
            urlInputRef.current?.focus();
            return;
          }
          log.info('[StepProject] finish clicked');
          onFinish();
        }}
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            {/* Congruent with where Finish actually lands: a reachable URL opens
                in-app team review; a local URL falls back to the extension via
                the dashboard. Keeps the peak-moment prompt honest (no "Creating..."
                that implies nothing about the destination). */}
            {isLocalEntry ? 'Setting up your project...' : 'Opening your review...'}
          </span>
        ) : isLastStep ? (
          <span className="flex items-center justify-center gap-2">
            Finish
            <HugeiconsIcon icon={Tick02Icon} size={16} strokeWidth={2} color="currentColor" className="h-4 w-4" />
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            Continue
            <HugeiconsIcon icon={ArrowRight01Icon} size={16} strokeWidth={2} color="currentColor" className="h-4 w-4" />
          </span>
        )}
      </Button>

      <div className="pt-2">
        <Button
          variant="ghost"
          size="md"
          onClick={() => {
            log.info('[StepProject] back clicked');
            onBack();
          }}
          disabled={isSubmitting}
        >
          <span className="flex items-center gap-2">
            <HugeiconsIcon icon={ArrowLeft01Icon} size={16} strokeWidth={2} color="currentColor" className="h-4 w-4" />
            Back
          </span>
        </Button>
      </div>
    </div>
  );
}
